import { cacheByTickCount } from "../Caching";
import { applyTransformations } from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { DataStore } from "../generic/DataStore";
import { Identifiable } from "../generic/Identifiable";
import { PlayerContextState } from "../PlayerContext";
import {
  getResourceAmount,
  getResourceCap,
  grantResource,
  Resource,
} from "../Resources";
import { registerTimeTickListener } from "./TimeTick";

export type IncomeOverTimeCalculator = (
  state: PlayerContextState,
) => Partial<Record<Resource, number>>;

// If something produces (or consumes) income over time,
// register it here. This will make it visible in the UI later.
export class IncomeOverTimeProducer implements Identifiable {
  id: string;
  calculator: IncomeOverTimeCalculator;

  constructor(id: string, calculator: IncomeOverTimeCalculator) {
    this.id = id;
    this.calculator = calculator;
  }

  getId(): string {
    return this.id;
  }

  getCalculator(): IncomeOverTimeCalculator {
    return this.calculator;
  }
}

const IncomeOverTimeProducers = new DataStore<IncomeOverTimeProducer>();

// This method does not apply income if the resource is capped,
// which is a "smarter" behavior.
function onTick(
  state: PlayerContextState,
  elapsedTimeSec: number,
): PlayerContextState {
  const incomes = IncomeOverTimeProducers.getAll().map((producer) =>
    producer.getCalculator()(state),
  );

  let changed = false;
  const incomeSet = new Set(incomes);
  const appliedTotals: Partial<Record<Resource, number>> = {};
  const appliedIncomes = new Set<Partial<Record<Resource, number>>>();
  do {
    changed = false;
    for (let resString of Object.values(Resource)) {
      const resource = resString as Resource;
      if (
        getResourceAmount(state, resource) + (appliedTotals?.[resource] ?? 0) <
        getResourceCap(state, resource)
      ) {
        // Apply all incomes for this resource
        const incomesThatHaveResource = Array.from(incomeSet).filter(
          (income) => (income?.[resource] ?? 0) > 0,
        );
        incomesThatHaveResource.forEach((income) => {
          let canApply = true;
          for (let subResString in income) {
            const subResource = subResString as Resource;
            const subResourceIncome =
              (income[subResource] ?? 0) * elapsedTimeSec;
            if (
              subResourceIncome < 0 &&
              getResourceAmount(state, subResource) +
                (appliedTotals?.[subResource] ?? 0) <
                -subResourceIncome
            ) {
              canApply = false;
            }
          }
          if (canApply) {
            appliedIncomes.add(income);
            for (let subResString in income) {
              const subResource = subResString as Resource;
              appliedTotals[subResource] =
                (appliedTotals?.[subResource] ?? 0) +
                (income[subResource] ?? 0) * elapsedTimeSec;
            }
          }
        });
        if (appliedIncomes.size > 0) {
          appliedIncomes.forEach((appliedIncome) =>
            incomeSet.delete(appliedIncome),
          );
          appliedIncomes.clear();
          changed = true;
        }
      }
    }
  } while (changed && incomeSet.size > 0);

  // Now we have calculated the total incomes, grant the resources
  for (let resString in appliedTotals) {
    if ((appliedTotals?.[resString as Resource] ?? 0) != 0) {
      state = grantResource(
        resString as Resource,
        appliedTotals?.[resString as Resource] ?? 0,
      )(state);
    }
  }

  return state;
}

function calculatedIncomesCached(
  state: PlayerContextState,
  readCacheOnly: boolean,
): Partial<Record<Resource, number>>[] {
  return cacheByTickCount(
    state,
    (s) => "incomePerSec",
    (state) =>
      IncomeOverTimeProducers.getAll().map((producer) =>
        producer.getCalculator()(state),
      ),
    readCacheOnly,
  );
}

// This method applies all incomes even if resources are capped.
function onTickApplyAll(
  state: PlayerContextState,
  elapsedTimeSec: number,
): PlayerContextState {
  if (elapsedTimeSec <= 0) {
    return state;
  }

  const incomes = calculatedIncomesCached(state, false);

  const appliedTotals: Partial<Record<Resource, number>> = {};
  for (let income of incomes) {
    let canApply = true;
    for (let subResString in income) {
      const subResource = subResString as Resource;
      const subResourceIncome = (income[subResource] ?? 0) * elapsedTimeSec;
      if (
        subResourceIncome < 0 &&
        getResourceAmount(state, subResource) < -subResourceIncome
      ) {
        canApply = false;
      }
    }

    if (canApply) {
      for (let subResString in income) {
        const subResource = subResString as Resource;
        const subResourceIncome = (income[subResource] ?? 0) * elapsedTimeSec;
        appliedTotals[subResource] =
          (appliedTotals?.[subResource] ?? 0) + subResourceIncome;
      }
    }
  }

  // Transform the totals
  for (let resource in appliedTotals) {
    appliedTotals[resource as Resource] =
      applyTransformations(
        [TransformationTags.FinalIncomePerSec, resource],
        state,
        (appliedTotals[resource as Resource] ?? 0) / elapsedTimeSec,
      ) * elapsedTimeSec;
  }

  // Now we have calculated the total incomes, grant the resources
  for (let resString in appliedTotals) {
    if ((appliedTotals?.[resString as Resource] ?? 0) != 0) {
      state = grantResource(
        resString as Resource,
        appliedTotals?.[resString as Resource] ?? 0,
      )(state);
    }
  }

  return state;
}

registerTimeTickListener("IncomeOverTime", onTickApplyAll);

// Only use for display purposes
export function calculateIncomePerSecond(
  state: PlayerContextState,
): Partial<Record<Resource, number>> {
  let total: Partial<Record<Resource, number>> = {};
  const incomes = calculatedIncomesCached(state, true);
  incomes.forEach((income) => {
    let canApply = true;
    for (let subResString in income) {
      const subResource = subResString as Resource;
      const subResourceIncome = (income[subResource] ?? 0) * 0.05;
      if (
        subResourceIncome < 0 &&
        getResourceAmount(state, subResource) < -subResourceIncome
      ) {
        canApply = false;
      }
    }

    // Skip incomes that cannot be applied
    if (canApply) {
      for (let resString in income) {
        const resource = resString as Resource;
        const existingIncome = total?.[resource] ?? 0;
        total[resource] = existingIncome + (income?.[resource] ?? 0);
      }
    }
  });

  // Transform the totals
  for (let resource in total) {
    total[resource as Resource] = applyTransformations(
      [TransformationTags.FinalIncomePerSec, resource],
      state,
      total[resource as Resource] ?? 0,
    );
  }
  return total;
}

export { IncomeOverTimeProducers };
